import { useCallback, useSyncExternalStore } from 'react';
import { mutateData } from '../../../api/client';
import i18n from '../../../i18n';

interface Preferences {
  locale: string;
  currency: string;
  dateFormat: string;
  numberFormat: string;
  timezone: string;
}

interface AvailableOptions {
  locales: string[];
  currencies: string[];
  dateFormats: string[];
  numberFormats: string[];
  timezones: string[];
}

const STORAGE_KEY = 'finlab-preferences';

const DEFAULT_PREFERENCES: Preferences = {
  locale: 'en',
  currency: 'SEK',
  timezone: 'Europe/Stockholm',
  dateFormat: 'YYYY-MM-DD',
  numberFormat: 'sv-SE',
};

const AVAILABLE_OPTIONS: AvailableOptions = {
  locales: ['en', 'sv', 'es', 'de', 'fr', 'fi', 'no', 'da'],
  currencies: ['SEK', 'EUR', 'USD', 'GBP', 'NOK', 'DKK'],
  timezones: [
    'Europe/Stockholm',
    'Europe/London',
    'Europe/Berlin',
    'Europe/Paris',
    'Europe/Helsinki',
    'Europe/Oslo',
    'Europe/Copenhagen',
    'America/New_York',
    'America/Chicago',
    'America/Los_Angeles',
    'Asia/Tokyo',
    'UTC',
  ],
  dateFormats: ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY', 'DD.MM.YYYY'],
  numberFormats: ['sv-SE', 'en-US', 'de-DE', 'fr-FR'],
};

// External store for useSyncExternalStore
let listeners: Array<() => void> = [];
let snapshot: Preferences | null = null;

function getStoredPreferences(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_PREFERENCES, ...JSON.parse(raw) };
  } catch {
    // ignore parse errors
  }
  return DEFAULT_PREFERENCES;
}

function setStoredPreferences(prefs: Preferences): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  snapshot = prefs;
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function getSnapshot(): Preferences {
  if (!snapshot) {
    snapshot = getStoredPreferences();
  }
  return snapshot;
}

/**
 * Seed localStorage preferences from the user's API profile on first login.
 * Only writes fields that differ from defaults (i.e. the user has set them in the DB
 * but localStorage hasn't been populated yet — e.g. Google-auth first login).
 * Subsequent changes flow from localStorage → API (fire-and-forget in updatePreferences).
 */
export function syncFromProfile(profile: {
  locale?: string;
  currency?: string;
  timezone?: string;
  dateFormat?: string;
  numberFormat?: string;
}): void {
  const stored = getStoredPreferences();
  const updates: Partial<Preferences> = {};

  // Only sync if localStorage still has defaults and profile has something different
  if (profile.locale && stored.locale === DEFAULT_PREFERENCES.locale && profile.locale !== stored.locale) {
    updates.locale = profile.locale;
  }
  if (profile.currency && stored.currency === DEFAULT_PREFERENCES.currency && profile.currency !== stored.currency) {
    updates.currency = profile.currency;
  }
  if (profile.timezone && stored.timezone === DEFAULT_PREFERENCES.timezone && profile.timezone !== stored.timezone) {
    updates.timezone = profile.timezone;
  }
  if (profile.dateFormat && stored.dateFormat === DEFAULT_PREFERENCES.dateFormat && profile.dateFormat !== stored.dateFormat) {
    updates.dateFormat = profile.dateFormat;
  }
  if (profile.numberFormat && stored.numberFormat === DEFAULT_PREFERENCES.numberFormat && profile.numberFormat !== stored.numberFormat) {
    updates.numberFormat = profile.numberFormat;
  }

  if (Object.keys(updates).length === 0) return;

  const merged = { ...stored, ...updates };
  setStoredPreferences(merged);

  // Sync i18n language immediately so the UI updates
  if (updates.locale && i18n.language !== updates.locale) {
    i18n.changeLanguage(updates.locale);
  }
}

export function usePreferences() {
  const preferences = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const updatePreferences = useCallback((updates: Partial<Preferences>) => {
    const current = getStoredPreferences();
    const updated = { ...current, ...updates };
    setStoredPreferences(updated);

    // Sync i18n language
    if (updates.locale && i18n.language !== updates.locale) {
      i18n.changeLanguage(updates.locale);
    }

    // Persist to backend (fire-and-forget for speed; tests wait for the response)
    mutateData('/api/auth/me', 'PUT', updates).catch(() => {
      // Silently ignore errors - localStorage is the source of truth
    });
  }, []);

  return {
    preferences,
    available: AVAILABLE_OPTIONS,
    isLoading: false,
    isSaving: false,
    updatePreferences,
  };
}
